/* @flow */
import * as React from 'react';
import pluralize from 'pluralize';
import { graphql, DataProps } from 'react-apollo';
import Modal from 'react-modal';

import { mountModal } from 'lib/modal';
import ConfirmDialog from 'components/ConfirmDialog';
import ErrorReporter from 'app/components/ErrorReporter';
import Group from 'entities/Group';
import GroupName from 'groups/components/GroupName';
import GroupSelectionDialog from 'groups/components/GroupSelectionDialog';
import LoadingModal from 'components/LoadingModal';
import OwnedGroupSummariesFetcher from 'groups/containers/OwnedGroupSummariesFetcher';
import Relationship from 'entities/Relationship';
import Subscriber from 'entities/Subscriber';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';

import ADD_MEMBERS_TO_GROUP_MUTATION from 'groups/components/mutations/addMembersToGroup.gql';

import type {
  addMembersToGroupVariables,
  addMembersToGroup_addUsersToGroup,
} from 'groups/components/mutations/__generated__/addMembersToGroup';
import type { LoadOwnedGroupSummaries_me_classes } from 'groups/queries/__generated__/LoadOwnedGroupSummaries';

type Props = {
  relationships: Relationship[],
  onSubscribe?: (groupUuid: string) => void,
  addMembersToGroup: ({ variables: addMembersToGroupVariables }) => Promise<
    DataProps<addMembersToGroup_addUsersToGroup>
  >,
  onRequestHide: () => void,
};

type State = {
  loadingModalOpen: boolean,
};

class CopySubscriberDialog extends React.Component<Props, State> {
  state: State = {
    loadingModalOpen: false,
  };

  getGroupsCopyableTo = (groups: LoadOwnedGroupSummaries_me_classes[]): Group[] => {
    const { relationships } = this.props;
    const filteredGroups = groups.filter((group: LoadOwnedGroupSummaries_me_classes) => {
      // filter out all groups where every user is already subscribed
      return relationships.every(relationship => {
        const subscriptions = relationship.getSubscriptionsToMe();

        return !subscriptions.some(subscription => {
          return subscription.getGroupId() === group.id && !group.staffClassOrgId;
        });
      });
    });
    return filteredGroups.map(group => ((Group.fromJSON(group): any): Group));
  };

  subscriberDisplayText = (subscribers: Subscriber[]) => {
    if (subscribers.length > 1) {
      return `${subscribers.length} users`;
    } else {
      return subscribers[0].getName();
    }
  };

  addSubscribers = (group: Group, subscribers: Subscriber[]) => {
    const { addMembersToGroup, onSubscribe, onRequestHide } = this.props;

    const userUuids = subscribers.map(sub => sub.getUuid());
    const groupUuid = group.getUuid();
    const groupName = group.getName();

    this.setState({ loadingModalOpen: true });

    // eslint-disable-next-line promise/catch-or-return
    addMembersToGroup({ variables: { input: { groupUuid, uuids: userUuids } } })
      .then(({ data }) => {
        if (data.addUsersToGroup.error) {
          ToastActions.showErrorToast(
            `Unable to copy ${this.subscriberDisplayText(subscribers)} to ${groupName}`
          );
        } else {
          ToastActions.showSuccessToast(
            `${this.subscriberDisplayText(subscribers)} ${pluralize(
              'was',
              subscribers.length
            )} copied to ${groupName}`
          );

          if (onSubscribe && groupUuid) {
            onSubscribe(groupUuid);
          }
        }
      })
      .catch(() => {
        ToastActions.showErrorToast(
          `Unable to copy ${this.subscriberDisplayText(subscribers)} to ${groupName}`
        );
      })
      .finally(() => {
        onRequestHide();
      });
  };

  confirmCopySubscriberToGroup = (group: Group) => {
    const { relationships } = this.props;
    const subscribers = relationships.map(relationship => relationship.getRelatedUser());

    mountModal(
      <ConfirmDialog
        eventScreenName="CopySubscriberDialog"
        title={
          <span>
            Are you sure you want to add{' '}
            <Text weight="semibold">{this.subscriberDisplayText(subscribers)}</Text> to{' '}
            <GroupName group={group} />?
          </span>
        }
        onConfirm={() => this.addSubscribers(group, subscribers)}
        actionButtonText="Add"
        cancelButtonText="Cancel"
      />,
      this
    );
  };

  renderEmptyMessage = (subscribers: Subscriber[], hasOwnedGroups: boolean) => {
    if (hasOwnedGroups) {
      return `${this.subscriberDisplayText(subscribers)} ${pluralize(
        'has',
        subscribers.length
      )} joined all of your classes.`;
    } else {
      return `You don't have any groups for ${this.subscriberDisplayText(
        subscribers
      )} to join yet.`;
    }
  };

  render() {
    const subscribers = this.props.relationships.map(relationship => relationship.getRelatedUser());
    const { loadingModalOpen } = this.state;

    return (
      <OwnedGroupSummariesFetcher>
        {({ ownedGroups, loading, error }) => {
          if (loading) {
            return <LoadingModal />;
          }

          if (error || !ownedGroups) {
            return <ErrorReporter error="CopySubscriberDialog - could not fetch owned groups" />;
          }

          const emptyMessage = this.renderEmptyMessage(subscribers, ownedGroups.length > 0);
          return (
            <React.Fragment>
              <GroupSelectionDialog
                {...this.props}
                title={`Add ${this.subscriberDisplayText(subscribers)} to another class`}
                groups={this.getGroupsCopyableTo(ownedGroups)}
                onSelect={this.confirmCopySubscriberToGroup}
                emptyMessage={emptyMessage}
              />
              <Modal isOpen={loadingModalOpen}>
                <LoadingModal />
              </Modal>
            </React.Fragment>
          );
        }}
      </OwnedGroupSummariesFetcher>
    );
  }
}

const withAddMembersMutation = graphql(ADD_MEMBERS_TO_GROUP_MUTATION, {
  name: 'addMembersToGroup',
});

export { CopySubscriberDialog };
export default withAddMembersMutation(CopySubscriberDialog);
